import { useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, UserFavoriteWithArche } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

interface FavoritePlacement {
  archId: string;
  position: number;
}

interface FavoritesData {
  favorites: UserFavoriteWithArche[];
}

/**
 * Full favorites management hook used by the Favorites page.
 * Provides the list, toggle, and placement-based reorder functionality.
 */
export function useFavorites() {
  const queryClient = useQueryClient();
  const favoritesQueryKey = queryKeys.favorites.list();

  const { data, isLoading, refetch } = useQuery({
    queryKey: favoritesQueryKey,
    queryFn: () => api.getFavorites(),
  });

  const favorites = data?.favorites ?? [];

  const favoriteIds = useMemo(() => new Set(favorites.map((f) => f.archId)), [favorites]);

  const isFavorite = useCallback((archId: string) => favoriteIds.has(archId), [favoriteIds]);

  const toggleMut = useMutation({
    mutationFn: async (archId: string) => {
      if (favoriteIds.has(archId)) {
        await api.removeFavorite(archId);
      } else {
        await api.addFavorite(archId);
      }
    },
    onMutate: async (archId: string) => {
      await queryClient.cancelQueries({ queryKey: favoritesQueryKey });
      const previous = queryClient.getQueryData<FavoritesData>(favoritesQueryKey);
      if (favoriteIds.has(archId)) {
        // Optimistic remove
        queryClient.setQueryData<FavoritesData>(favoritesQueryKey, (old) => ({
          favorites: (old?.favorites ?? []).filter((f) => f.archId !== archId),
        }));
      }
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(favoritesQueryKey, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: favoritesQueryKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.favorites.all });
    },
  });

  const reorderMut = useMutation({
    mutationFn: (placements: FavoritePlacement[]) => api.reorderFavorites(placements),
    onMutate: async (placements: FavoritePlacement[]) => {
      await queryClient.cancelQueries({ queryKey: favoritesQueryKey });
      const previous = queryClient.getQueryData<FavoritesData>(favoritesQueryKey);
      const placementMap = new Map(placements.map((p) => [p.archId, p.position]));
      queryClient.setQueryData<FavoritesData>(favoritesQueryKey, (old) => ({
        favorites: (old?.favorites ?? [])
          .map((f) => {
            const newPos = placementMap.get(f.archId);
            return newPos !== undefined ? { ...f, position: newPos } : f;
          })
          .sort((a, b) => a.position - b.position),
      }));
      return { previous };
    },
    onError: (_err, _vars, context) => {
      queryClient.setQueryData(favoritesQueryKey, context?.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: favoritesQueryKey });
    },
  });

  return {
    favorites,
    isLoading,
    isFavorite,
    toggleFavorite: async (archId: string) => {
      await toggleMut.mutateAsync(archId);
    },
    reorder: async (placements: FavoritePlacement[]) => {
      await reorderMut.mutateAsync(placements);
    },
    refresh: () => {
      refetch();
    },
  };
}
